import React, { useState } from "react";

const AI = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Gain a comprehensive
          understanding of Artificial Intelligence (AI) concepts and apply them
          using Python to solve real-world problems.
          <p className="font-bold text-sm">Duration:</p> 30 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic knowledge of
          Python programming, mathematics (linear algebra, statistics), and an
          interest in AI and machine learning.
          <p className="font-bold text-sm">Tools Needed:</p> Python 3.x, Jupyter
          Notebook, NumPy, Pandas, Scikit-learn, TensorFlow, Keras, and a code
          editor (e.g., VS Code).
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Artificial Intelligence",
          objective: "Understand the fundamentals of AI and its applications.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  What is AI? History and types (narrow, general, super AI).
                </li>
                <li>Applications of AI in daily life.</li>
                <li>Setting up the Python environment for AI.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Install Python, Jupyter Notebook, and required libraries.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Python Refresher for AI",
          objective: "Review Python essentials for AI development.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Python data structures (lists, dictionaries).</li>
                <li>Functions and modules.</li>
                <li>Working with external libraries.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a Python script to process sample data.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Introduction to NumPy for AI",
          objective: "Learn NumPy for numerical operations in AI.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>NumPy arrays and operations.</li>
                <li>Matrix manipulations and broadcasting.</li>
                <li>Efficient computation techniques.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform matrix operations using NumPy.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Data Handling with Pandas",
          objective: "Use Pandas for data manipulation in AI projects.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Series and DataFrames in Pandas.</li>
                <li>Loading datasets and basic exploration.</li>
                <li>Filtering and grouping data.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Load and explore a dataset using Pandas.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Data Cleaning Techniques",
          objective: "Master data cleaning for AI model preparation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Handling missing data and duplicates.</li>
                <li>Outlier detection and treatment.</li>
                <li>Data normalization and scaling.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Clean a dataset with missing values and outliers.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Data Visualization Basics",
          objective: "Visualize data using Matplotlib.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Importance of visualization in AI.</li>
                <li>Creating scatter plots, line plots, and bar charts.</li>
                <li>Customizing visualizations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a scatter plot to analyze data trends.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Advanced Visualization with Seaborn",
          objective: "Enhance visualizations using Seaborn.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Seaborn for statistical visualizations.</li>
                <li>Box plots, violin plots, and pair plots.</li>
                <li>Styling for better presentation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Generate a pair plot for a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Foundations of Machine Learning for AI",
          objective: "Understand machine learning concepts for AI.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Supervised vs. unsupervised learning.</li>
                <li>Role of machine learning in AI.</li>
                <li>Overview of Scikit-learn library.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Explore Scikit-learn documentation.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Linear Regression for Prediction",
          objective: "Implement linear regression for AI tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Linear regression fundamentals.</li>
                <li>Gradient descent and cost functions.</li>
                <li>Building a model with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train a linear regression model on a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Logistic Regression for Classification",
          objective: "Learn logistic regression for AI classification tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Logistic regression basics.</li>
                <li>Sigmoid function and decision boundaries.</li>
                <li>Implementation with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a logistic regression classifier.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Decision Trees in AI",
          objective: "Explore decision trees for AI applications.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Decision tree algorithm overview.</li>
                <li>Entropy and information gain.</li>
                <li>Training a decision tree with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a decision tree model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Random Forests for Robust AI Models",
          objective: "Learn ensemble methods with random forests.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to random forests.</li>
                <li>Bagging and feature selection.</li>
                <li>Implementation with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train a random forest classifier.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: K-Nearest Neighbors (KNN) for AI",
          objective: "Apply KNN for classification tasks in AI.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>KNN algorithm overview.</li>
                <li>Distance metrics and selecting k.</li>
                <li>Implementation with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a KNN model for classification.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Support Vector Machines (SVM) in AI",
          objective: "Use SVM for complex classification tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>SVM basics and kernel functions.</li>
                <li>Hyperplanes and margin maximization.</li>
                <li>Implementation with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train an SVM classifier.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Introduction to Neural Networks",
          objective: "Understand the basics of neural networks for AI.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What are neural networks?</li>
                <li>Neurons, layers, and activation functions.</li>
                <li>Overview of TensorFlow and Keras.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install TensorFlow and create a simple neural network.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: Building a Simple Neural Network",
          objective: "Implement a basic neural network for AI tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Structure of a neural network (input, hidden, output layers).
                </li>
                <li>Forward and backward propagation.</li>
                <li>Training a neural network with Keras.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train a neural network on a simple dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Introduction to Deep Learning",
          objective: "Explore deep learning concepts for AI.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deep learning vs. traditional machine learning.</li>
                <li>Deep neural networks and architectures.</li>
                <li>Applications of deep learning in AI.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Explore a pre-trained deep learning model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Convolutional Neural Networks (CNNs)",
          objective: "Learn CNNs for image-related AI tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to CNNs and their applications.</li>
                <li>Convolution, pooling, and fully connected layers.</li>
                <li>Building a CNN with Keras.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a simple CNN for image classification.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Natural Language Processing (NLP) Basics",
          objective: "Understand NLP fundamentals for AI.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to NLP and its role in AI.</li>
                <li>
                  Text preprocessing (tokenization, stemming, lemmatization).
                </li>
                <li>Using NLTK or spaCy for NLP tasks.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preprocess a text dataset for NLP.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Word Embeddings for NLP",
          objective: "Learn word embeddings for AI text processing.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What are word embeddings?</li>
                <li>Using pre-trained embeddings (e.g., Word2Vec, GloVe).</li>
                <li>Implementing embeddings with Keras.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Use word embeddings for a text classification task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Recurrent Neural Networks (RNNs)",
          objective: "Explore RNNs for sequential data in AI.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to RNNs and their applications.</li>
                <li>LSTMs and GRUs for long-term dependencies.</li>
                <li>Building an RNN with Keras.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train an RNN for a text sequence task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Reinforcement Learning Basics",
          objective: "Understand reinforcement learning concepts for AI.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is reinforcement learning?</li>
                <li>Agents, environments, rewards, and policies.</li>
                <li>Introduction to OpenAI Gym.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up OpenAI Gym and run a simple environment.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Q-Learning for Reinforcement Learning",
          objective: "Implement Q-learning for AI decision-making.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Q-learning.</li>
                <li>Q-table and exploration vs. exploitation.</li>
                <li>Implementing Q-learning in Python.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a Q-learning agent for a simple game.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Introduction to Computer Vision",
          objective: "Learn computer vision basics for AI applications.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is computer vision?</li>
                <li>Image processing with OpenCV.</li>
                <li>Basic operations (resizing, filtering, edge detection).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform edge detection on an image using OpenCV.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Model Evaluation and Ethics in AI",
          objective:
            "Evaluate AI models and understand ethical considerations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Evaluation metrics (accuracy, precision, recall, F1-score).
                </li>
                <li>Ethical issues in AI (bias, fairness, transparency).</li>
                <li>Best practices for responsible AI.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Evaluate an AI model and discuss ethical implications.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Final Project – Planning",
          objective: "Plan the final AI project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>
                  Suggested project ideas (e.g., chatbot, image classifier).
                </li>
                <li>Planning data collection and model selection.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and dataset outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Final Project – Data Collection and Preprocessing",
          objective: "Collect and preprocess data for the AI project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Sourcing data (APIs, datasets, web scraping).</li>
                <li>Data cleaning and preprocessing.</li>
                <li>Exploratory data analysis for insights.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Prepare the project dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Final Project – Model Development",
          objective: "Develop and train the AI model for the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Selecting the appropriate AI model (e.g., CNN, RNN).</li>
                <li>Training and fine-tuning the model.</li>
                <li>Evaluating model performance.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement and train the project model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Results and Visualization",
          objective: "Visualize results and derive insights from the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating visualizations for model results.</li>
                <li>Interpreting and analyzing outcomes.</li>
                <li>Documenting findings and insights.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Visualize and summarize project results.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – Presentation",
          objective: "Present the completed AI project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a live demo or report.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Presenting to peers or instructors.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the final AI project.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Understand and apply AI concepts using Python.</li>
            <li>
              Build and evaluate machine learning and deep learning models.
            </li>
            <li>
              Develop AI solutions for real-world problems (e.g., image
              recognition, NLP).
            </li>
            <li>
              Complete an end-to-end AI project with a focus on ethics and
              responsibility.
            </li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Chatbot Development:</span>{" "}
              Build a simple chatbot using NLP and RNNs.
            </li>
            <li>
              <span className="font-bold text-sm">Image Classifier:</span>{" "}
              Create a CNN-based model to classify images.
            </li>
            <li>
              <span className="font-bold text-sm">Game AI Agent:</span> Develop
              an AI agent using reinforcement learning to play a simple game.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              Practice with real-world datasets (e.g., Kaggle, UCI ML
              Repository).
            </li>
            <li>
              Explore additional resources (e.g., TensorFlow tutorials, OpenAI
              documentation).
            </li>
            <li>
              Use discussion time for troubleshooting and exploring advanced
              topics.
            </li>
          </ul>
          <p className="text-sm">
            This syllabus provides a structured progression from AI basics to
            advanced applications over 30 days.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-red-600 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">
          Artificial Intelligence (AI) Syllabus
        </h1>
        <p className="text-sm text-white">
          Master AI with Python (30 Hours – 1 Hour Per Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AI;
